import { createUseSquadsMultisig, type SquadsMultisigStatus } from "@shared/yaas/squadsMultisig";
import { useWallet } from "@solomon-apps/shared";

import { useRetailAccountStore } from "./retailAccountStore";
import { preflightRetailSquadsMultisig, squadsDestinationStatus } from "./retailSquadsPreflight";

export const useRetailSquadsLookup = createUseSquadsMultisig(preflightRetailSquadsMultisig);

interface RetailAccount {
    signerAddress: string;
    isSquads: boolean;
    address: string;
    vaultAddress: string;
    ready: boolean;
    status: SquadsMultisigStatus;
    notice: string | null;
    retry: () => void;
    key: string;
}

export function useRetailAccount(): RetailAccount {
    const wallet = useWallet();
    const signerAddress = wallet.connected ? (wallet.publicKey?.toString() ?? "") : "";
    const squad = useRetailAccountStore((state) => state.selected[signerAddress] ?? null);
    const retryNonce = useRetailAccountStore((state) => state.retryNonce);
    const retry = useRetailAccountStore((state) => state.retry);
    const status = useRetailSquadsLookup(squad?.address ?? "", signerAddress || null, retryNonce);
    const verifiedVault =
        status.status === "success" && status.signerStatus === "can-verify" ? status.multisig.vaultAddress : undefined;
    const ready = !!verifiedVault;
    const isSquads = squad !== null;
    return {
        signerAddress,
        isSquads,
        address: squad?.address ?? signerAddress,
        vaultAddress: isSquads ? (verifiedVault ?? "") : signerAddress,
        ready: isSquads ? ready : !!signerAddress,
        status,
        notice:
            isSquads && !ready
                ? (squadsDestinationStatus(status, true).message ?? "Unable to verify this Squads vault")
                : null,
        retry,
        // Changing signer or selected account remounts the retail ceremony and its local state.
        key: `${signerAddress}:${squad?.address ?? "personal"}`
    };
}
